import { CSSProperties, useState, useEffect } from 'react'
import {
    ThinkingState,
} from 'Hooks/useMaestroChat'
import Colors from 'modules/Colors'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'

const ThinkingSection = ({
    thinkingState,
    initiallyOpen = false
}: {
    thinkingState: ThinkingState,
    initiallyOpen?: boolean
}) => {
    const [isOpen, setIsOpen] = useState(initiallyOpen)

    useEffect(() => {
        setIsOpen(initiallyOpen)
    }, [initiallyOpen])

    // Only show if there's thinking content or actively thinking
    if (!thinkingState.isThinking && !thinkingState.currentThinking) {
        return null
    }

    const sty: Record<string, CSSProperties> = {
        thinkingContainer: {
            marginBottom: 8,
            backgroundColor: '#f8fafc',
            borderRadius: 8,
            padding: '8px 12px',
            border: `1px solid #e2e8f0`,
        },
        thinkingHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            fontWeight: 500,
            color: '#64748b',
        },
        headerText: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
        },
        thinkingContent: {
            paddingTop: 8,
            fontSize: 13,
            lineHeight: 1.5,
            color: '#475569',
            fontStyle: 'italic',
            whiteSpace: 'pre-wrap',
            position: 'relative',
        },
        thinkingCursor: {
            animation: 'typingAnimation 1s infinite',
            color: '#64748b',
            fontSize: 14,
            marginLeft: 2,
        },
    }

    return (
        <div className="thinkingContainer" style={sty.thinkingContainer}>
            <div
                className="thinkingHeader"
                style={sty.thinkingHeader}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div style={sty.headerText}>
                    <Icon
                        icon="gem"
                        size={16}
                        className="thinkingIcon"
                    />
                    <span>
                        {thinkingState.isThinking ? 'Maestro is thinking...' : 'Maestro\'s reasoning'}
                    </span>
                </div>
                <Icon
                    icon={isOpen ? 'chevronUp' : 'chevronDown'}
                    size={16}
                    className="thinkingToggleIcon"
                />
            </div>
            {isOpen && (
                <div className="thinkingContent" style={sty.thinkingContent}>
                    {thinkingState.currentThinking}
                    {thinkingState.isThinking && (
                        <span className="thinkingCursor" style={sty.thinkingCursor}>|</span>
                    )}
                </div>
            )}
        </div>
    )
}

export default ThinkingSection


const sty: Record<string, CSSProperties> = {
    assistantChatWrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
    },
    assistantChatComponent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    assistantChatComponent_mobile: {
        overflow: 'auto',
    },
    assistantChatComponent_chat: {
        height: '100%',
    },
    assistantChatComponent_question: {
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
        maxWidth: 800,
        margin: '0 auto',
        width: '100%',
    },
    assistantChatComponent_question_mobile: {
        justifyContent: 'flex-start',
    },
    assistantChatContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 800,
        margin: '0 auto',
        width: '100%',
        padding: '0 24px',
    },
    assistantChatContainer_chatMode: {
        padding: 0,
    },
    assistantChatInputContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        fontSize: 20,
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
        transition: 'all 0.2s ease',
        minHeight: 100,
        backgroundColor: Colors.greyLight,
        outline: 'none',
        maxWidth: 600,
        width: '100%',
        marginBottom: 24
    },
    assistantChatInputContainer_mobile: {
        marginBottom: 16,
        minHeight: 'unset',

    },
    assistantChatInputContainer_chatMode: {
        borderRadius: 8,
        width: '100%',
        maxWidth: 'unset',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
    assistantChatInputContainer_chatMode_mobile: {
        marginBottom: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
    assistantChatInputContainerWrapper: {
        padding: 24,
        borderTop: Sty.border,
        marginTop: 0,
        marginBottom: 24,
        position: 'relative',
        zIndex: 999, // Above workspace modal
        backgroundColor: 'white',
        maxWidth: 800,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },

    assistantChatInputContainerWrapper_mobile: {
        marginBottom: 16,
    },

    assistantChatInputContainerWrapper_chatMode_mobile: {
        padding: 0,
        position: 'sticky',
        bottom: 0,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: 0,
    },
    assistantChatInput: {
        flex: 1,
        fontSize: 18,
        resize: 'none',
        minHeight: 40,
        maxHeight: 120,
        outline: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        lineHeight: 1.4,
        marginBottom: 8,
    },
    assistantChatCharacterInfo: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        minHeight: 16,
        marginBottom: 8,
        fontSize: 12,
    },
    characterCount: {
        color: Colors.greyDark,
        fontSize: 12,
    },
    characterCountWarning: {
        color: '#f59e0b', // Orange warning color
        fontSize: 12,
        fontWeight: 500,
    },
    characterLimitWarning: {
        color: Colors.red,
        fontSize: 12,
        fontWeight: 600,
        animation: 'fadeInUp 0.2s ease-out',
    },
    assistantChatQuestion: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 24
    },
    assistantChatQuestion_mobile: {
        marginBottom: 16

    },
    questionDevModeToggle: {
        position: 'absolute',
        top: 24,
        right: 24,
        zIndex: 10,
    },
    assistantChatMessages: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 0',
        overflowY: 'auto',
        gap: 24,
        flex: 1,
        minHeight: 0,
        scrollBehavior: 'smooth', // Enable smooth scrolling
    },
    assistantChatMessages_mobile: {
        paddingLeft: 8,
        paddingRight: 8,
    },
    message: {
        display: 'flex',
        maxWidth: '80%',
        padding: 12,
        borderRadius: 16,
    },
    userMessage: {
        alignSelf: 'flex-end',
        backgroundColor: Colors.purple,
        color: 'white',
        margin: '0 24px',
        marginRight: 0,
        fontSize: 18
    },
    assistantMessage: {
        alignSelf: 'flex-start',
        backgroundColor: 'transparent',
        color: Colors.textColor,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        maxWidth: '100%',
        padding: '0 24px',
        borderRadius: 0,
        paddingLeft: 0,
        fontSize: 18,
    },
    messageContent: {
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.6,
        flex: 1,
        paddingRight: 8,
        position: 'relative',
        marginTop: 5

    },

    thinkingMessage: {
        fontStyle: 'italic',
        color: Colors.greyDark,
    },
    assistantChatActionButton: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: 8,
    },
    assistantChatTabsContainer: {
        width: 750,
        margin: '0 auto',
    },
    assistantChatTabsContainer_mobile: {
        width: '100%',
    },
    assistantChatSendButton: {
        flexShrink: 0,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: 8,
        borderRadius: 6,
        backgroundColor: Colors.purple,
        color: 'white',
    },
    mediaContainer: {
        marginBottom: 8,
        borderRadius: 8,
        overflow: 'hidden',
        maxWidth: '100%',
    },
    mediaThumbnail: {
        maxWidth: '100%',
        maxHeight: 200,
        objectFit: 'cover',
        cursor: 'pointer',
        display: 'block'
    },
   
    videoContainer: {
        position: 'relative',
        display: 'inline-block',
        cursor: 'pointer',
    },
    playOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '50%',
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    playIcon: {
        color: 'white',
    },
    assistantChatInputActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 'auto',
    },
    // playbookTagsContainer: { // Commented out as its usage is commented out
    //     display: 'flex',
    //     flexWrap: 'wrap',
    //     gap: 8,
    // },
    newChatButton: {
        cursor: 'pointer',
        backgroundColor: 'transparent',
        border: 'none',
        color: Colors.greyDark,
        fontSize: 14,
        fontFamily: 'inherit',
        padding: '4px 8px',
        transition: 'all 0.2s ease',
        borderRadius: 4,
    },
    newChatButtonTopRight: {
        position: 'absolute',
        top: 8,
        right: 8,
        fontSize: 12,
        color: Colors.greyDark,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: '4px 8px',
        borderRadius: 12,
        border: `1px solid ${Colors.grey}`,
    },
    /** Styles for loading indicators */
    loadingTextWithIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        backgroundColor: Colors.greyLight,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 14,
        fontStyle: 'italic',
        color: Colors.greyDark,
        marginBottom: 4,
    },
    loadingIndicator: {
        animation: 'spin 1s linear infinite',
    },
    loadingIndicatorPulse: {
        animation: 'pulse 1.5s ease-in-out infinite',
    },
    loadingDots: {
        display: 'flex',
        gap: 4,
        alignItems: 'center',
    },
    loadingDot: {
        animation: 'bounce 1.4s ease-in-out infinite both',
        fontSize: 20,
        lineHeight: 1,
        color: Colors.greyDark,
    },
    /** Workspace styles */
    assistantChatWithWorkspace: {
        display: 'flex',
        height: '100%',
        gap: 24,
        maxWidth: '80%',
        margin: 'auto'
    },
    assistantChatWithWorkspace_mobile: {
        maxWidth: '100%',
    },
    assistantChatSection: {
        flex: '0 0 600px', // Fixed width for chat section
        minWidth: 400,
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
    },
    assistantWorkspaceSection: {
        flex: 1, // Take up remaining space
        minWidth: 0, // Allow shrinking
        borderLeft: Sty.border,
        paddingLeft: 24,
    },
    assistantWorkspaceReport: {
        width: '100%',
    },
    assistantWorkspaceReportContent: {
        backgroundColor: Colors.greyLight,
        padding: 16,
        borderRadius: 8,
        fontSize: 12,
        fontFamily: 'monospace',
        overflow: 'auto',
        maxHeight: 400,
        margin: 0,
    },
    assistantWorkspacePreview: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    assistantWorkspacePreviewImage: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
    },
    assistantWorkspacePreviewContent: {
        padding: 12,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        fontSize: 14,
        lineHeight: 1.5,
    },
    assistantWorkspaceComponent: {
        width: '100%',
        minHeight: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `2px dashed ${Colors.grey}`,
        color: Colors.greyDark,
        fontSize: 14,
    },
    assistantWorkspaceCustom: {
        width: '100%',
    },
    /** Top bar styles for chat mode */
    assistantChatTopBar: {
        position: 'sticky',
        top: 0,
        width: '100%',
        height: 60,
        backgroundColor: 'white',
        borderBottom: `1px solid ${Colors.grey}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 800,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        flexShrink: 0,
    },
    assistantChatTopBarLeft: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
    },
    assistantChatTopBarCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
    assistantChatTopBarRight: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flex: 1,
    },
    assistantChatBackButton: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: 16,
        color: Colors.textColor,
        padding: '8px 12px',
        borderRadius: 6,
        transition: 'all 0.2s ease',
    },
    assistantChatNewChatButton: {
        backgroundColor: Colors.purple,
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: 20,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    workspaceButton: {
        marginTop: 8,
        marginRight: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
    },
    workspaceButtonsContainer: {
        marginTop: 12,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    assistantWorkspaceModal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000, // Above messages but below chat input
        padding: 16,
    },
    assistantWorkspaceModal_mobile: {
        padding: 0,
        alignItems: 'flex-end', // Slide up from bottom on mobile
    },
    assistantWorkspaceModalBackdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: 'pointer',
    },
    assistantWorkspaceModalContent: {
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: 12,
        maxWidth: '90vw',
        maxHeight: '85vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        zIndex: 1001,
        marginBottom: 120, // Space for chat input
    },
    assistantWorkspaceModalContent_mobile: {
        maxWidth: '100vw',
        maxHeight: '100%', // Account for top bar + chat input
        borderRadius: '12px 12px 0 0',
        marginBottom: 0
    },
    assistantWorkspaceModalContentInner: {
        flex: 1,
        overflowY: 'auto',
        padding: 24,
        paddingTop: 16,
    },
    assistantWorkspaceModalContentInner_mobile: {
        padding: 16,
        paddingTop: 12,
    },
    /** System message styles */
    systemMessagesInlineWithIcon: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 8,
    },
    systemMessageInline: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        padding: '4px 8px',
        fontSize: 12,
        color: Colors.greyDark,
        border: `1px solid ${Colors.grey}`,
        alignSelf: 'flex-start',
        maxWidth: '100%',
        fontStyle: 'italic',
    },
    systemMessage_router_start: {
        backgroundColor: '#f0f9ff',
        borderColor: '#0ea5e9',
        color: '#0369a1',
    },
    systemMessage_specialist_activated: {
        backgroundColor: '#f0fdf4',
        borderColor: '#22c55e',
        color: '#15803d',
    },
    systemMessage_tool_call_start: {
        backgroundColor: '#fefce8',
        borderColor: '#eab308',
        color: '#a16207',
    },
    systemMessage_tool_call_end: {
        backgroundColor: '#f0fdf4',
        borderColor: '#22c55e',
        color: '#15803d',
        animation: 'none',
    },

    systemMessageText: {
        fontWeight: 500,
    },
    /** Tool execution styles */
    toolExecutionContainer: {
        padding: '0 24px',
        marginBottom: 16,
    },
    toolExecutionMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        backgroundColor: '#fef3c7',
        borderRadius: 12,
        padding: '8px 12px',
        fontSize: 14,
        color: '#92400e',
        border: '1px solid #f59e0b',
    },
    toolExecutionIcon: {
        flexShrink: 0,
        animation: 'spin 1s linear infinite',
    },
    toolExecutionText: {
        fontWeight: 500,
        flex: 1,
    },
    toolExecutionDots: {
        display: 'flex',
        gap: 2,
        alignItems: 'center',
    },
    workspaceReopenButtons: {
        padding: 16,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        marginBottom: 16,
    },
    workspaceReopenTitle: {
        fontSize: 18,
        fontWeight: 600,
        marginBottom: 12,
    },
    workspaceReopenButtonsList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    workspaceReopenButton: {
        backgroundColor: Colors.purple,
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    persistentSystemMessages: {
        padding: '0 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginTop: 16,
        alignItems: 'flex-start'
    },
    summaryCard: {
        display: 'flex',
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        maxWidth: 400,
    },
    summaryCard_wide: {
        flexDirection: 'column',
        maxWidth: 300,
    },
    summaryCardLink: {
        textDecoration: 'none',
        color: 'inherit',
    },
    summaryCardImage: {
        width: 80,
        height: 80,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardImage_wide: {
        width: '100%',
        height: 120,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardImage_tall: {
        width: 60,
        height: 100,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardContent: {
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
    },
    summaryCardTitle: {
        fontWeight: 'bold',
        fontSize: 16,
        marginBottom: 4,
        color: Colors.textColor,
    },
    summaryCardSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },
    quickActionsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        animation: 'fadeInUp 0.3s ease-out',
    },
    quickActionButton: {
        border: 'none',
        padding: '12px 20px',
        borderRadius: 8,
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: 14,
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        animation: 'fadeInUp 0.3s ease-out',
        animationFillMode: 'both',
    },
    quickActionButton_primary: {
        backgroundColor: Colors.purple,
        color: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    quickActionButton_secondary: {
        backgroundColor: 'transparent',
        color: Colors.purple,
        border: `1px solid ${Colors.purple}`,
    },
    quickActionsWrapper: {
        padding: 16,
        marginBottom: 16,
        marginLeft: 24,
        marginRight: 24,
    },
    // Base card styles for all video generation states
    videoGenerationInlineCard: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
        borderRadius: 12,
        border: `1px solid ${Colors.grey}`,
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        animation: 'fadeInUp 0.4s ease-out',
    },

    // In-progress state styles
    videoGenerationInlineInProgress: {
        padding: 16,
        gap: 12,
    },
    videoGenerationInlineInProgress_hover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    },

    // Completed state styles
    videoGenerationInlineCompleted: {
        padding: 0,
        cursor: 'pointer',
        maxWidth: 300,
    },
    videoGenerationInlineCompleted_hover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    },

    // Aspect ratio specific styles for completed state
    videoGenerationInlineCompleted_wide: {
        maxWidth: 400,
    },
    videoGenerationInlineCompleted_square: {
        maxWidth: 320,
    },
    videoGenerationInlineCompleted_tall: {
        maxWidth: 280,
    },

    // Video container styles
    videoGenerationInlineVideoContainer: {
        position: 'relative',
        overflow: 'hidden',
    },
    videoGenerationInlineVideoContainer_wide: {
        aspectRatio: '16/9',
    },
    videoGenerationInlineVideoContainer_square: {
        aspectRatio: '1/1',
    },
    videoGenerationInlineVideoContainer_tall: {
        aspectRatio: '9/16',
    },

    videoGenerationInlineVideoThumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'all 0.3s ease',
    },

    videoGenerationInlinePlayOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '50%',
        width: 56,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
    },

    videoGenerationInlinePlayIcon: {
        color: 'white',
        marginLeft: 3, // Slight adjustment for visual centering
    },

    videoGenerationInlineSuccessBadge: {
        position: 'absolute',
        top: 12,
        right: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        backgroundColor: Colors.green,
        color: 'white',
        padding: '6px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },

    videoGenerationInlineSuccessIcon: {
        color: 'white',
    },

    videoGenerationInlineSuccessText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 600,
    },

    videoGenerationInlineVideoInfo: {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    videoGenerationInlineVideoTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.textColor,
    },

    videoGenerationInlineVideoSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },

    videoGenerationInlineVideoStats: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: Colors.greyDark,
    },

    videoGenerationInlineStatsText: {
        fontSize: 12,
        color: Colors.greyDark,
    },

    // Failed state styles
    videoGenerationInlineFailed: {
        padding: 20,
        gap: 16,
        alignItems: 'center',
        textAlign: 'center',
        borderColor: Colors.red,
        backgroundColor: '#fef2f2',
    },
    videoGenerationInlineFailed_hover: {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
    },

    videoGenerationInlineFailedIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    videoGenerationInlineFailedIconSvg: {
        color: Colors.red,
    },

    videoGenerationInlineFailedInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    videoGenerationInlineFailedTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.red,
    },

    videoGenerationInlineFailedSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },

    // Legacy styles (keeping for backward compatibility)
    videoGenerationInline: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 8,
    },
    videoGenerationInline_hover: {
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        padding: '4px 8px',
    },
    videoGenerationInlineHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        fontWeight: 500,
        color: Colors.textColor,
    },
    videoGenerationInlineTitleSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    videoGenerationInlineIcon: {
        marginRight: 6,
    },
    videoGenerationInlineTitles: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    videoGenerationInlineTitle: {
        fontWeight: 'bold',
        fontSize: 16,
    },
    videoGenerationInlineSubtitle: {
        fontSize: 14,
    },
    videoGenerationInlineProgress: {
        marginTop: 4,
        marginBottom: 4,
        height: 12,
        backgroundColor: Colors.greyLight,
        borderRadius: 6,
        overflow: 'hidden',
    },
    videoGenerationInlineProgressBar: {
        height: '100%',
        backgroundColor: Colors.purple,
    },
    videoGenerationInlineProgressFill: {
        height: '100%',
        backgroundColor: Colors.purple,
    },
    videoGenerationInlineProgressText: {
        fontWeight: 500,
        fontSize: 14,
        textAlign: 'right',
        paddingRight: 4,
    },
    videoGenerationInlineStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    videoGenerationInlineStatusIcon: {
        marginRight: 4,
    },
    videoGenerationInlineStatusText: {
        fontWeight: 500,
        fontSize: 14,
    },
    videoGenerationInlineEstimate: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    videoGenerationInlineEstimateIcon: {
        marginRight: 4,
    },
    videoGenerationInlineEstimateText: {
        fontWeight: 500,
        fontSize: 14,
    },
    /** Music selector modal styles */
    musicSelectorModal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200, // Above workspace modal
        padding: 16,
    },
    musicSelectorModal_mobile: {
        padding: 0,
        alignItems: 'flex-end',
    },
    musicSelectorModalBackdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: 'pointer',
    },
    musicSelectorModalContent: {
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: 12,
        maxWidth: '90vw',
        maxHeight: '85vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        zIndex: 1201,
    },
    musicSelectorModalContent_mobile: {
        maxWidth: '100vw',
        maxHeight: '90vh',
        borderRadius: '12px 12px 0 0',
    },
    musicSelectorModalHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        borderBottom: `1px solid ${Colors.grey}`,
        backgroundColor: 'white',
    },
    musicSelectorModalTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: Colors.textColor,
    },
    musicSelectorModalCloseButton: {
        padding: '8px 12px',
        color: Colors.greyDark,
    },
    musicSelectorModalContentInner: {
        flex: 1,
        overflowY: 'auto',
        padding: 0,
    },
    /** Test Workspace styles */
    testWorkspaceContainer: {
        marginBottom: 16,
        paddingBottom: 16,
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    testWorkspaceButton: {
        backgroundColor: Colors.greyLight,
        color: Colors.textColor,
        border: `1px solid ${Colors.grey}`,
        padding: '8px 16px',
        borderRadius: 6,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    testWorkspaceModalContent: {
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    testWorkspaceModalDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        lineHeight: 1.5,
    },
    testWorkspaceInput: {
        width: '100%',
        padding: '12px 16px',
        fontSize: 16,
        border: `1px solid ${Colors.grey}`,
        borderRadius: 6,
        outline: 'none',
        transition: 'border-color 0.2s ease',
    },
    /** Dev Mode styles */
    devModeToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    devModeLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.textColor,
    },
    devModeToggleButton: {
        position: 'relative',
        width: 44,
        height: 24,
        borderRadius: 12,
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        outline: 'none',
    },
    devModeToggleButton_active: {
        backgroundColor: Colors.purple,
    },
    devModeToggleButton_inactive: {
        backgroundColor: Colors.grey,
    },
    devModeToggleSlider: {
        position: 'absolute',
        top: 2,
        width: 20,
        height: 20,
        borderRadius: '50%',
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    devModeToggleSlider_active: {
        left: 22,
    },
    devModeToggleSlider_inactive: {
        left: 2,
    },
    messageDevActions: {
        display: 'flex',
        gap: 6,
        marginTop: 8,
        justifyContent: 'flex-end',
        opacity: 1, // Always visible in dev mode
        transition: 'opacity 0.2s ease',
    },
    messageDevActionButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        minWidth: 20,
        minHeight: 20,
        padding: 0,
    },
    messageDevActionButton_delete: {
        backgroundColor: Colors.red,
        color: 'white',
    },
    messageDevActionButton_regenerate: {
        backgroundColor: Colors.blue,
        color: 'white',
    },
    messageDevActionButton_resend: {
        backgroundColor: Colors.purple,
        color: 'white',
    },
    /** Job and Chat Item Styles */
    jobItem: {
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 12,
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    jobItemContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    jobItemLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    jobItemIcon: {
        fontSize: 24,
        flexShrink: 0,
    },
    jobItemInfo: {
        flex: 1,
        minWidth: 0,
    },
    jobItemTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.textColor,
        margin: 0,
        marginBottom: 4,
    },
    jobItemDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    jobItemRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
    },
    jobItemStatus: {
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
    },
    jobItemProgressContainer: {
        width: 60,
        height: 4,
        backgroundColor: Colors.greyLight,
        borderRadius: 2,
        overflow: 'hidden',
    },
    jobItemProgress: {
        height: '100%',
        borderRadius: 2,
        transition: 'width 0.3s ease',
    },
    jobItemFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
        paddingTop: 8,
        borderTop: `1px solid ${Colors.greyLight}`,
    },
    jobItemDate: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    jobItemChatLink: {
        fontSize: 12,
        color: Colors.purple,
        fontWeight: 500,
    },
    chatItem: {
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 12,
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    chatItemContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    chatItemLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    chatItemIcon: {
        fontSize: 24,
        flexShrink: 0,
    },
    chatItemInfo: {
        flex: 1,
        minWidth: 0,
    },
    chatItemTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.textColor,
        margin: 0,
        marginBottom: 4,
    },
    chatItemDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        margin: 0,
    },
    chatItemRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
    },
    chatItemStatus: {
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
    },
    chatItemFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
        paddingTop: 8,
        borderTop: `1px solid ${Colors.greyLight}`,
        fontSize: 12,
        color: Colors.greyDark,
    },
    chatItemDate: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    chatItemStarted: {
        fontSize: 12,
        color: Colors.greyDark,
    },
    chatItemTasks: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 8,
    },
    chatItemTask: {
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
    },
    chatItemTask_campaign: {
        backgroundColor: Colors.purple + '20',
        color: Colors.purple,
    },
    chatItemTask_asset: {
        backgroundColor: Colors.blue + '20',
        color: Colors.blue,
    },
    /** Tab Content Styles */
    tabContent: {
        padding: '24px 0',
        minHeight: 300,
    },
    tabContentContainer: {
        height: 400, // Fixed height for the tab content area
        width: '100%',
        position: 'relative',
    },
    tabContentScrollable: {
        height: '100%',
        overflowY: 'auto',
        padding: '16px 0',
    },
    itemsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 48,
        gap: 12,
        color: Colors.greyDark,
    },
    loadingIcon: {
        animation: 'pulse 1.5s ease-in-out infinite',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 48,
        gap: 12,
        textAlign: 'center',
    },
    emptyIcon: {
        color: Colors.greyDark,
        opacity: 0.5,
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: Colors.textColor,
        margin: 0,
    },
    emptyDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        margin: 0,
        maxWidth: 300,
    },
}